import { useEffect, useState } from 'react';

import { useForm } from 'react-hook-form';

import { getErrorMessageForRequiredFields, removeNonDigits } from 'utils/stringUtils';
import { isValidEmail, validatePhoneNumber } from 'utils/validationUtils';
import { checkBirthDate } from 'utils/dateUtils';
import { useAppSelector } from 'hooks/useAppSelector';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { preQualificationApi } from 'api/PreQualification/PreQualificationApi';
import { PhoneLineType } from 'api/PreQualification/preQualificationApi.enums';
import { LoanInformationVariables } from 'enums/LoanInformationVariables';
import {
  EMAIL_CONFIRMATION,
  INVALID_PHONE_LINE_TYPE,
} from 'pages/PreQualification/steps/LoanInformation/loanInformation.constants';
import { setPrequalificationPortal } from 'actions/prequalificationPortal';
import { BorrowerIdentityFormType } from 'pages/PreQualification/steps/BorrowerIdentity/borrowerIdentity.types';

export const useBorrowerIdentityForm = (submitForm: (value: BorrowerIdentityFormType) => Promise<void>) => {
  const dispatch = useAppDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const borrowerState = useAppSelector((state) => state.borrower);
  const { confirmBorrowerEmail, isNeedToClearLoanInformation, isBorrowerAuthorized } = useAppSelector(
    (state) => state.prequalificationPortal,
  );
  const {
    register,
    unregister,
    formState: { errors, isSubmitted },
    setValue,
    trigger,
    watch,
    clearErrors,
    handleSubmit,
    reset,
    setError,
    control,
  } = useForm<BorrowerIdentityFormType>({
    defaultValues: {
      [LoanInformationVariables.BorrowerCellPhone]: borrowerState.phone,
      [LoanInformationVariables.BorrowerDateOfBirth]: borrowerState.dateOfBirth,
      [LoanInformationVariables.BorrowerEmail]: borrowerState.email,
      [LoanInformationVariables.ConfirmBorrowerEmail]: confirmBorrowerEmail,
      [LoanInformationVariables.FullConsentCheckbox]: false,
    },
  });

  const watcher = watch();

  useEffect(() => {
    register(LoanInformationVariables.BorrowerCellPhone, {
      required: getErrorMessageForRequiredFields(LoanInformationVariables.BorrowerCellPhone),
      validate: (value) => {
        return validatePhoneNumber(value);
      },
    });
    register(LoanInformationVariables.BorrowerDateOfBirth, {
      required: getErrorMessageForRequiredFields(LoanInformationVariables.BorrowerDateOfBirth),
      validate: (value) => checkBirthDate(value) || 'Please enter a valid date',
    });
    register(LoanInformationVariables.BorrowerEmail, {
      required: getErrorMessageForRequiredFields(LoanInformationVariables.BorrowerEmail),
      validate: (value) => {
        return isValidEmail(value) || 'Please enter a valid email address';
      },
    });
    register(LoanInformationVariables.ConfirmBorrowerEmail, {
      required: !isBorrowerAuthorized && getErrorMessageForRequiredFields(EMAIL_CONFIRMATION),
      validate: (value) =>
        !!isBorrowerAuthorized ||
        !watcher[LoanInformationVariables.BorrowerEmail] ||
        !watcher[LoanInformationVariables.ConfirmBorrowerEmail] ||
        value?.toLowerCase() === watcher[LoanInformationVariables.BorrowerEmail] ||
        'Entered Email does not match Borrower Email',
    });
    register(LoanInformationVariables.FullConsentCheckbox, {
      required: false,
      validate: (value) => {
        return true;
      },
    });
  }, [register, unregister, watcher, isBorrowerAuthorized]);

  useEffect(() => {
    if (isNeedToClearLoanInformation) {
      reset({});
      dispatch(setPrequalificationPortal({ isNeedToClearLoanInformation: false }));
    }
  }, [dispatch, reset, isNeedToClearLoanInformation]);

  const checkBorrowersPhonesLineType = async () => {
    try {
      const phoneLineTypes = await preQualificationApi.getPhoneNumberLineType({
        borrowerPhoneNumber: removeNonDigits(watcher[LoanInformationVariables.BorrowerCellPhone]),
      });

      let phoneVerificationPassed = true;

      if (phoneLineTypes.borrowerLineType !== PhoneLineType.Mobile) {
        phoneVerificationPassed = false;

        setError(LoanInformationVariables.BorrowerCellPhone, {
          type: 'manual',
          message: INVALID_PHONE_LINE_TYPE,
        });
      }

      return phoneVerificationPassed;
    } catch (error) {
      // if prove return error during checking phone line type we don't stop the flow
      return true;
    }
  };

  const handleSubmitLoanForm = handleSubmit(async () => {
    setIsLoading(true);

    const result = await trigger();
    const isValidPhoneLineType = await checkBorrowersPhonesLineType();

    if (isValidPhoneLineType && result) {
      await submitForm(watcher);
    }

    setIsLoading(false);
  });

  return {
    register: register,
    unregister: unregister,
    errors: errors,
    isSubmitted: isSubmitted,
    setValue: setValue,
    trigger: trigger,
    watcher: watcher,
    handleSubmitLoanForm: handleSubmitLoanForm,
    reset: reset,
    clearErrors: clearErrors,
    checkBorrowersPhonesLineType: checkBorrowersPhonesLineType,
    isLoading: isLoading,
    control: control,
  };
};
